import { CampaignData } from '../../core/models/campaign'
import Modal from '../ui/Modal'
import { 
    EyeIcon, 
    ShoppingBagIcon, 
    QrCodeIcon, 
    CalendarIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import { useTranslation } from '../../core/hooks/useTranslation'
import EditButton from '../buttons/EditButton'
import DeleteButton from '../buttons/DeleteButton'
import QRCode from 'react-qr-code'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'

interface CampaignCardProps {
    campaign: CampaignData
    onEdit?: (campaign: CampaignData) => void
    onDelete?: (campaignId: number) => void
    showEditButton?: boolean
    showDeleteButton?: boolean
    className?: string
}

export default function CampaignCard({
    campaign,
    onEdit,
    onDelete,
    showEditButton = true,
    showDeleteButton = true,
    className = ''
}: CampaignCardProps) {
    const { t, isRTL } = useTranslation()
    const qrCodeContainerRef = useRef<HTMLDivElement>(null)
    const modalQrCodeRef = useRef<HTMLDivElement>(null)
    const [showQRModal, setShowQRModal] = useState(false)

    const isActive = campaign.isActive && 
        (!campaign.startDate || new Date(campaign.startDate) <= new Date()) &&
        (!campaign.endDate || new Date(campaign.endDate) >= new Date())

    const stats = campaign.statistics || {
        totalVisits: 0,
        totalOrders: 0,
        totalScans: 0,
        recentVisits: 0,
        recentOrders: 0
    }

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            calendar: 'gregory'
        })
    }

    const handleDownloadQR = (fromModal = false) => {
        const containerRef = fromModal ? modalQrCodeRef : qrCodeContainerRef
        if (!containerRef.current) return

        try {
            // Get SVG element from container
            const svg = containerRef.current.querySelector('svg')
            if (!svg) {
                toast.error(t('error') || 'Failed to find QR code')
                return
            }

            const svgData = new XMLSerializer().serializeToString(svg)
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            const img = new Image()

            img.onload = () => {
                if (!ctx) return

                // High quality dimensions for media use (print quality)
                // SVG will be scaled up, maintaining quality
                const qrSize = 1200 // Large QR code size for high quality
                const padding = 100 // Padding around QR code
                const headerHeight = 120 // Height for campaign name at top
                const footerHeight = 160 // Height for branding at bottom
                const totalWidth = qrSize + (padding * 2)
                const totalHeight = qrSize + (padding * 2) + headerHeight + footerHeight

                canvas.width = totalWidth
                canvas.height = totalHeight

                // Enable image smoothing for better quality when scaling
                ctx.imageSmoothingEnabled = true
                ctx.imageSmoothingQuality = 'high'

                // Fill white background
                ctx.fillStyle = '#FFFFFF'
                ctx.fillRect(0, 0, canvas.width, canvas.height)

                // Draw QR code in center with padding (scaled up with high quality)
                ctx.drawImage(img, padding, padding + headerHeight, qrSize, qrSize)

                const siteName = t('siteName') || 'Hatli'
                const siteDescription = t('siteDescription') || 'Platform for menu and order management'
                const websiteUrl = window.location.origin

                // Draw campaign name at top
                if (campaign.name) {
                    ctx.font = 'bold 40px Arial, sans-serif'
                    ctx.fillStyle = '#1E293B'
                    ctx.textAlign = 'center'
                    ctx.textBaseline = 'middle'
                    ctx.fillText(campaign.name, totalWidth / 2, padding + headerHeight / 2)
                }

                // Draw site name and branding at bottom
                ctx.font = 'bold 56px Arial, sans-serif'
                ctx.fillStyle = '#1E293B' // Navy blue color
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText(siteName, totalWidth / 2, qrSize + padding + headerHeight + 50)

                // Draw site description
                ctx.font = '24px Arial, sans-serif'
                ctx.fillStyle = '#64748B' // Gray color
                ctx.fillText(siteDescription, totalWidth / 2, qrSize + padding + headerHeight + 95)

                // Draw website URL
                ctx.font = '18px Arial, sans-serif'
                ctx.fillStyle = '#94A3B8' // Lighter gray
                ctx.fillText(websiteUrl, totalWidth / 2, qrSize + padding + headerHeight + 130)

                // Convert to PNG and download with maximum quality
                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = `hatli-qr-code-${campaign.name.replace(/\s+/g, '-')}-${campaign.qrCode}.png`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        URL.revokeObjectURL(url)
                        toast.success(t('qrCodeDownloaded') || 'QR Code downloaded successfully')
                    }
                }, 'image/png', 1.0) // Maximum quality
            }

            img.onerror = () => {
                toast.error(t('error') || 'Failed to download QR code')
            }

            // Create image from SVG - SVG is vector so it scales perfectly
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(svgBlob)
            img.src = url
        } catch (error) {
            console.error('Error downloading QR code:', error)
            toast.error(t('error') || 'Failed to download QR code')
        }
    }

    return (
        <div className={`relative bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200 flex flex-col h-full overflow-hidden ${className}`}>
            {/* Header */}
            <div className={`px-4 py-3 border-b ${isActive ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{campaign.name}</h3>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => setShowQRModal(true)}
                            className="p-1.5 rounded-md hover:bg-white/60 transition-colors"
                            title={t('viewQRCode') || 'عرض QR Code'}
                        >
                            <QrCodeIcon className="w-5 h-5 text-gray-600 hover:text-blue-600 transition-colors" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Dates */}
            {(campaign.startDate || campaign.endDate) && (
                <div className={`px-5 py-3 border-b border-gray-100 bg-white flex items-center justify-between gap-4 text-xs text-gray-500`}>
                    {campaign.startDate && (
                        <div className="flex items-center gap-1.5">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            <span>{t('startDate')}: {formatDate(campaign.startDate)}</span>
                        </div>
                    )}
                    {campaign.endDate && (
                        <div className="flex items-center gap-1.5">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            <span>{t('endDate')}: {formatDate(campaign.endDate)}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="flex-1 px-5 py-5 space-y-4">
                {/* Statistics */}
                <div className="space-y-2">
                    {/* Visits - لوحدها */}
                    <div className="flex items-center justify-between gap-2.5 p-2 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 rounded-md">
                                <EyeIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-xs text-gray-600">{t('visits') || 'Visits'}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">{stats.totalVisits.toLocaleString()}</span>
                    </div>
                    
                    {/* Orders & Scans - مع بعض */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center justify-between gap-2.5 p-2 bg-green-50 rounded-lg border border-green-100">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-green-100 rounded-md">
                                    <ShoppingBagIcon className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="text-xs text-gray-600">{t('orders') || 'Orders'}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-900">{stats.totalOrders.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2.5 p-2 bg-purple-50 rounded-lg border border-purple-100">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-purple-100 rounded-md">
                                    <QrCodeIcon className="w-4 h-4 text-purple-600" />
                                </div>
                                <span className="text-xs text-gray-600">{t('scans') || 'Scans'}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-900">{stats.totalScans.toLocaleString()}</span>
                        </div>
                    </div>
                </div>
                
                {/* Description */}
                {campaign.description && (
                    <div>
                        <p className="text-sm text-gray-600">{campaign.description}</p>
                    </div>
                )}
            </div>

            {/* Hidden QR Code for download functionality */}
            <div 
                ref={qrCodeContainerRef} 
                className="hidden"
            >
                <QRCode
                    value={campaign.qrCode}
                    size={120}
                    style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                />
            </div>

            {/* Actions */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2">
                <button
                    onClick={() => handleDownloadQR(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                    title="تحميل QR"
                >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                    <span>تحميل QR</span>
                </button>
                {showEditButton && onEdit && (
                    <EditButton onClick={onEdit} item={campaign} className="rounded-md" />
                )}
                {showDeleteButton && onDelete && (
                    <DeleteButton onClick={onDelete} itemOrId={campaign.id} className="rounded-md" />
                )}
            </div>

            {/* QR Code Modal */}
            <Modal
                isOpen={showQRModal}
                onClose={() => setShowQRModal(false)}
                title={campaign.name}
                description={campaign.description}
                showCancelButton={true}
                cancelButtonText={t('close') || 'إغلاق'}
                actionButtonText="تحميل QR"
                onAction={() => {
                    handleDownloadQR(true)
                    setShowQRModal(false)
                }}
            >
                <div className="flex flex-col items-center gap-4 py-4">
                    <div 
                        ref={modalQrCodeRef} 
                        className="bg-white p-4 rounded-lg border-2 border-gray-200"
                    >
                        <QRCode
                            value={campaign.qrCode}
                            size={250}
                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                        />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <QrCodeIcon className="w-4 h-4" />
                        <span className="font-mono">{campaign.qrCode}</span>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

